import type { FC, ReactNode } from 'react'
import type { ListingSummary } from '@/types'

import { useRef, useState } from 'react'
import { Grid2x2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { listingPhotos } from '@/components/homes/listing-card'
import { Photo } from '@/components/generals/photo'

interface PhotoMosaicProps {
    listing: ListingSummary
    onOpen: (index: number) => void
}

/* Airbnb-style photo grid on larger screens, swipeable photos on phones. */
export const PhotoMosaic: FC<PhotoMosaicProps> = ({ listing, onOpen }): ReactNode => {
    const photos = listingPhotos(listing)
    const shown = photos.slice(0, 5)
    const scrollerRef = useRef<HTMLDivElement>(null)
    const [index, setIndex] = useState(0)

    const desktopLayout = [
        '',
        'grid-cols-1',
        'grid-cols-[2fr_1fr]',
        'grid-cols-[2fr_1fr] grid-rows-2',
        'grid-cols-[2fr_1fr_1fr] grid-rows-2',
        'grid-cols-4 grid-rows-2'
    ][shown.length] ?? 'grid-cols-4 grid-rows-2'

    const tileClass = (tileIndex: number) => {
        if (tileIndex === 0) {
            if (shown.length === 5) return 'col-span-2 row-span-2'

            return shown.length >= 3 ? 'row-span-2' : ''
        }

        if (shown.length === 4 && tileIndex === 3) return 'col-span-2'

        return ''
    }

    if (!photos.length) {
        return (
            <div className='relative aspect-[16/9] overflow-hidden rounded-2xl bg-muted'>
                <Photo src={null} alt={listing.title} sizes='100vw' />
            </div>
        )
    }

    return (
        <div id='photos'>
            <div className='relative -mx-4 sm:-mx-6 md:hidden'>
                <div
                    ref={scrollerRef}
                    onScroll={() => {
                        const scroller = scrollerRef.current

                        if (scroller?.clientWidth) setIndex(Math.round(scroller.scrollLeft / scroller.clientWidth))
                    }}
                    className='no-scrollbar flex aspect-[4/3] snap-x snap-mandatory overflow-x-auto'
                >
                    {photos.map((photo, photoIndex) => (
                        <button
                            key={photo.id}
                            type='button'
                            onClick={() => onOpen(photoIndex)}
                            className='relative h-full w-full shrink-0 snap-center'
                            aria-label={`Open photo ${photoIndex + 1} of ${photos.length}`}
                        >
                            <Photo src={photo.image} alt={photo.image_alt} sizes='100vw' priority={photoIndex === 0} />
                        </button>
                    ))}
                </div>

                <span className='absolute right-4 bottom-4 rounded-md bg-black/60 px-2.5 py-1 text-xs font-semibold text-white tabular-nums'>
                    <span>{index + 1}</span> / <span>{photos.length}</span>
                </span>
            </div>

            <div className={cn('relative hidden h-[26rem] gap-2 overflow-hidden rounded-2xl md:grid lg:h-[30rem]', desktopLayout)}>
                {shown.map((photo, tileIndex) => (
                    <button
                        key={photo.id}
                        type='button'
                        onClick={() => onOpen(tileIndex)}
                        className={cn('group relative overflow-hidden bg-muted focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring focus-visible:ring-inset', tileClass(tileIndex))}
                        aria-label={`Open photo ${tileIndex + 1} of ${photos.length}`}
                    >
                        <Photo
                            src={photo.image}
                            alt={photo.image_alt}
                            sizes={tileIndex === 0 ? '(min-width: 1280px) 50vw, 66vw' : '25vw'}
                            priority={tileIndex === 0}
                            className='transition duration-300 group-hover:brightness-90'
                        />
                    </button>
                ))}

                <button
                    type='button'
                    onClick={() => onOpen(-1)}
                    className='absolute right-5 bottom-5 flex items-center gap-2 rounded-lg border border-foreground bg-white px-4 py-2 text-sm font-semibold shadow-sm transition hover:bg-secondary'
                >
                    <Grid2x2 className='size-4' aria-hidden='true' />
                    Show all photos
                </button>
            </div>
        </div>
    )
}
